import { useMemo } from "react";
import { useAuth } from "@/lib/auth";
import { shouldSuppressLicenseCtasForPlatformDesigner } from "@/lib/accountStatus";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { useWorkspaceMode } from "@/lib/workspaceMode";
import { useEntitlements } from "@/modules/useEntitlements";
import { usePlatformDesignerBuyerPreview } from "@/lib/usePlatformDesignerBuyerPreview";
import { applyBuyerPreviewSystemInfoOverlay } from "@/lib/platformDesignerBuyerPreview";
import PanelLicenseActivationCtas from "@/components/PanelLicenseActivationCtas";
import {
  LICENSE_CONTACT_ADMIN_MESSAGE,
  resolveLicenseActivationUx,
} from "@/modules/panelLicenseActivationCtas";

/**
 * Bloque CTAs / mensaje contacto admin — misma lógica en Panel General y hubs inicio.
 * No montar en rutas operativas (/clientes, etc.).
 */
export default function PanelLicenseActivationBlock({ className = "" }) {
  const { user } = useAuth();
  const isPlatformDesigner = usePlatformDesigner();
  const buyerPreviewActive = usePlatformDesignerBuyerPreview();
  const { mode, canSwitch } = useWorkspaceMode();
  const { systemInfo } = useEntitlements({});

  const displaySystemInfo = useMemo(() => {
    if (systemInfo) return systemInfo;
    if (buyerPreviewActive) return applyBuyerPreviewSystemInfoOverlay(null);
    return null;
  }, [systemInfo, buyerPreviewActive]);

  const isDistributorWorkspace =
    !isPlatformDesigner
    && canSwitch
    && mode === "distribuidor"
    && Boolean(user?.can_generate_licenses);

  const activationUx = useMemo(
    () =>
      resolveLicenseActivationUx({
        user,
        systemInfo: displaySystemInfo,
        workspaceMode: mode,
        canSwitch,
        buyerPreviewUx: buyerPreviewActive,
      }),
    [user, displaySystemInfo, mode, canSwitch, buyerPreviewActive],
  );

  if (
    shouldSuppressLicenseCtasForPlatformDesigner(user, { buyerPreviewUx: buyerPreviewActive })
    || isDistributorWorkspace
  ) {
    return null;
  }
  if (!activationUx.show || !displaySystemInfo) {
    return null;
  }

  const wrapClass = ["w-full min-w-0", className].filter(Boolean).join(" ");

  if (activationUx.canRequest) {
    return (
      <div className={wrapClass} data-testid="panel-license-activation-hub-block">
        <PanelLicenseActivationCtas systemInfo={displaySystemInfo} />
      </div>
    );
  }

  if (activationUx.showContactMessage) {
    return (
      <div
        className={`${wrapClass} rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-950`}
        data-testid="panel-license-cta-contact-admin"
      >
        {LICENSE_CONTACT_ADMIN_MESSAGE}
      </div>
    );
  }

  return null;
}
